/**
 * Testing Service - API wrapper, preset CRUD, result export
 * TRNS-143: Internal Testing UI
 */

import { config } from '../config/env';
import {
  PipelineConfig,
  PromptWeighting,
  TestingContext,
  TestingPreset,
  TestingResult,
  TestingApiResponse,
} from '../types';

// localStorage keys
const STORAGE_KEYS = {
  PASSWORD: 'testing_password_verified',
  FORM_STATE: 'testing_form_state',
  PRESETS: 'testing_presets',
  RESULTS: 'testing_results',
};

const MAX_RESULTS = 50;

// ---- API ----

export async function runTranslation(params: {
  text: string;
  sourceLang: string;
  targetLang: string;
  mode: 'write' | 'understand' | 'be_understood';
  context: TestingContext;
  pipeline?: PipelineConfig;
  weighting?: PromptWeighting;
}): Promise<TestingApiResponse> {
  const url = `${config.api.url}/api/v1/translate`;

  const body: Record<string, unknown> = {
    text: params.text,
    sourceLanguage: params.sourceLang,
    targetLanguage: params.targetLang,
    mode: params.mode,
    context: {
      gender: params.context.gender || 'neutral',
      register: params.context.register || 'informal',
      tone: params.context.tone || 'casual',
      region: params.context.region || undefined,
    },
  };

  // Pass pipeline config as JSON string in the pipeline field (Option A from spec)
  if (params.pipeline) {
    body.pipeline = JSON.stringify({
      model: params.pipeline.model,
      temperature: params.pipeline.temperature,
      topP: params.pipeline.topP,
      maxTokens: params.pipeline.maxTokens,
    });
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.api.timeout);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errData = await response.json().catch(() => ({
        code: 'HTTP_ERROR',
        message: `HTTP ${response.status}: ${response.statusText}`,
      }));
      return { success: false, error: errData };
    }

    return await response.json();
  } catch (err) {
    clearTimeout(timeoutId);
    const message = err instanceof Error ? err.message : 'Unknown error';
    return { success: false, error: { code: 'NETWORK_ERROR', message } };
  }
}

export async function checkHealth(): Promise<{ status: string; timestamp: string } | null> {
  try {
    const url = `${config.api.url}/api/v1/translate/health`;
    const res = await fetch(url, { method: 'GET' });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

// ---- Presets (localStorage) ----

export function loadPresets(): TestingPreset[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.PRESETS);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function savePreset(preset: TestingPreset): void {
  const presets = loadPresets();
  presets.push(preset);
  localStorage.setItem(STORAGE_KEYS.PRESETS, JSON.stringify(presets));
}

export function deletePreset(id: string): void {
  const presets = loadPresets().filter((p) => p.id !== id);
  localStorage.setItem(STORAGE_KEYS.PRESETS, JSON.stringify(presets));
}

// ---- Results (localStorage, capped) ----

export function loadResults(): TestingResult[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.RESULTS);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveResult(result: TestingResult): void {
  const results = loadResults();
  results.unshift(result);
  // Cap at MAX_RESULTS
  if (results.length > MAX_RESULTS) {
    results.length = MAX_RESULTS;
  }
  localStorage.setItem(STORAGE_KEYS.RESULTS, JSON.stringify(results));
}

export function clearResults(): void {
  localStorage.removeItem(STORAGE_KEYS.RESULTS);
}

// ---- Form State Persistence ----

export function saveFormState(state: Record<string, unknown>): void {
  localStorage.setItem(STORAGE_KEYS.FORM_STATE, JSON.stringify(state));
}

export function loadFormState(): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.FORM_STATE);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// ---- Password Gate ----

export function isPasswordVerified(): boolean {
  return localStorage.getItem(STORAGE_KEYS.PASSWORD) === 'true';
}

export function setPasswordVerified(val: boolean): void {
  if (val) {
    localStorage.setItem(STORAGE_KEYS.PASSWORD, 'true');
  } else {
    localStorage.removeItem(STORAGE_KEYS.PASSWORD);
  }
}

// ---- Export ----

export function exportResultsToJSON(results: TestingResult[]): void {
  const blob = new Blob([JSON.stringify(results, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `translayshun-testing-results-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
